import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "../../styles/DeliveryModal.css";
import { apiGetMyProfile, apiUpdateMyDelivery } from "../../api/me.api";

const ANIM_MS = 250;

export default function DeliveryModal({ open, onClose, onSaved }) {
  const { t } = useTranslation("delivery");

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, ANIM_MS);
      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  useEffect(() => {
    if (!open) return;
    let alive = true;

    (async () => {
      setLoading(true);
      try {
        const d = await apiGetMyProfile();
        if (!alive) return;

        const p = d?.profile || {};
        setPhone(p.phone || "");
        setCity(p.city || "");
        setAddress(p.address || "");
      } catch (e) {
        console.warn("apiGetMyProfile failed:", e?.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open]);

  async function submit() {
    const ph = phone.trim();
    const ct = city.trim();
    const ad = address.trim();

    if (ph.length < 5) return alert(t("validation_phone"));
    if (!ct) return alert(t("validation_city"));
    if (ad.length < 3) return alert(t("validation_address"));

    setSaving(true);
    try {
      const r = await apiUpdateMyDelivery({ phone: ph, city: ct, address: ad });
      onSaved?.(r?.profile || { phone: ph, city: ct, address: ad });
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(e?.message || t("save_error"));
    } finally {
      setSaving(false);
    }
  }

  if (!mounted) return null;

  return (
    <div
      className={`dmOverlay ${open && !closing ? "isOpen" : "isClosing"}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="dmCard" onClick={(e) => e.stopPropagation()}>
        <div className="dmHeader">
          <div className="dmTitle">{t("title")}</div>
          <button className="dmClose" onClick={onClose} aria-label={t("close")}>
            <FiX />
          </button>
        </div>

        {loading ? (
          <div className="dmSub">{t("loading")}</div>
        ) : (
          <>
            <label className="dmLabel">
              {t("phone_label")}
              <input
                className="dmInput"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("phone_placeholder")}
                disabled={saving}
              />
            </label>

            <label className="dmLabel">
              {t("city_label")}
              <input
                className="dmInput"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("city_placeholder")}
                disabled={saving}
              />
            </label>

            <label className="dmLabel">
              {t("address_label")}
              <input
                className="dmInput"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t("address_placeholder")}
                disabled={saving}
              />
            </label>

            <button
              type="button"
              className="dmSave"
              onClick={submit}
              disabled={saving}
            >
              {saving ? t("saving") : t("save")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}